import React from "react";
import utils from "../../universal/utils";
import { KNOWN_CODENAMES } from "./registry";

const COOKIE_NAME = "ouro_tpl";
const COOKIE_MAX_AGE_DAYS = 30;
const DEFAULT_CODENAME = "original";

function getCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[$()*+./?[\\\]^{|}]/g, "\\$&") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name, value, days) {
  if (typeof document === "undefined") return;
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`;
}

function isValidCodename(value) {
  return (
    typeof value === "string" &&
    /^[a-z][a-z0-9_-]*$/.test(value) &&
    KNOWN_CODENAMES.includes(value)
  );
}

function isLocalHostname(hostname) {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname?.endsWith(".local")
  );
}

function canUseQueryOverride() {
  if (typeof window === "undefined") return false;
  return (
    process.env.NODE_ENV !== "production" ||
    isLocalHostname(window.location.hostname)
  );
}

/**
 * Pick a template from the active list using weighted random selection.
 * Weights default to 1 for any template not listed in weights map.
 */
function pickWeighted(activeTemplates, weights) {
  if (!activeTemplates || activeTemplates.length === 0) return DEFAULT_CODENAME;
  if (activeTemplates.length === 1) return activeTemplates[0];

  const totalWeight = activeTemplates.reduce((sum, name) => {
    return sum + (typeof weights[name] === "number" && weights[name] > 0 ? weights[name] : 1);
  }, 0);

  let rand = Math.random() * totalWeight;
  for (const name of activeTemplates) {
    const w = typeof weights[name] === "number" && weights[name] > 0 ? weights[name] : 1;
    rand -= w;
    if (rand <= 0) return name;
  }
  return activeTemplates[activeTemplates.length - 1];
}

function resolveTemplateFromConfig(config, stickyCodename) {
  if (!config || typeof config !== "object") return DEFAULT_CODENAME;

  const { mode, activeTemplates, weights = {} } = config;
  const active = Array.isArray(activeTemplates)
    ? activeTemplates.filter(isValidCodename)
    : [];

  if (active.length === 0) return DEFAULT_CODENAME;

  if (stickyCodename && active.includes(stickyCodename)) {
    return stickyCodename;
  }

  if (mode === "rotate") {
    return pickWeighted(active, weights);
  }

  return active[0];
}

/**
 * Returns the resolved template codename for this visitor.
 *
 * Resolution order:
 *  1. ?template= query param (dev/local only — ignored in production)
 *  2. Fetch config from edge
 *  3. Reuse a sticky cookie only if it is still active; otherwise re-pick
 *
 * Returns { codename: "original", isResolved: false } during SSR/first paint
 * so the shell can render the safe SEO fallback without recording an impression
 * until the visitor's actual template is known.
 */
export function useTemplateConfig(initialTemplate) {
  const [codename, setCodename] = React.useState(
    isValidCodename(initialTemplate?.codename)
      ? initialTemplate.codename
      : DEFAULT_CODENAME
  );
  const [isResolved, setIsResolved] = React.useState(
    initialTemplate?.isResolved === true
  );

  React.useEffect(() => {
    // 1. Dev override via query param
    if (canUseQueryOverride()) {
      try {
        const params = new URLSearchParams(window.location.search);
        const override = params.get("template");
        if (isValidCodename(override)) {
          setCodename(override);
          setIsResolved(true);
          return;
        }
      } catch (_) {}
    }

    // 2. Sticky cookie from a prior visit. We validate it against the live
    // config below so disabled templates stop serving without redeploy.
    const sticky = getCookie(COOKIE_NAME);

    // 3. Fetch from edge
    const basePath = typeof utils.getEdgeBasePath === "function"
      ? utils.getEdgeBasePath()
      : "/edge";

    fetch(`${basePath}/api/v1/public/template-config`, {
      credentials: "omit",
      cache: "no-store",
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data?.ok || !data?.config) return;

        const picked = resolveTemplateFromConfig(
          data.config,
          isValidCodename(sticky) ? sticky : null
        );

        setCookie(COOKIE_NAME, picked, COOKIE_MAX_AGE_DAYS);
        setCodename(picked);
        setIsResolved(true);
      })
      .catch(() => {
        // Fallback to original on any error — tracking must never crash.
        setIsResolved(true);
      });
  }, []);

  return { codename, isResolved };
}
